'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { useLocaleNavigation } from '@/lib/navigation';
import { Routes } from '@/constants/routes';
import {
    Container,
    Typography,
    Badge,
    Button,
    Card,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
} from '@/components/design-system';
import { Calendar, Clock, Tag, Home } from 'lucide-react';
import SchemaScript from '@/components/common/SchemaScript';
import { Locale } from '@/constants/locales';

export default function BlogPage() {
    const locale = useLocale();
    const t = useTranslations('blog');
    const { getNavigationPath } = useLocaleNavigation();

    const BLOG_POSTS = Object.values(Routes).filter((post) => {
        return post.pageType === 'blog' && post.isPublished;
    });

    return (
        <>
            <div className="min-h-screen bg-background">
                {/* Hero Section */}
                <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-muted/20 py-20">
                    {/* Animated background elements */}
                    <div className="absolute inset-0">
                        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-3xl animate-pulse" />
                        <div className="absolute top-40 right-20 w-96 h-96 bg-gradient-to-br from-secondary/20 to-transparent rounded-full blur-3xl animate-pulse delay-1000" />
                        <div className="absolute bottom-20 left-1/3 w-64 h-64 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl animate-pulse delay-2000" />
                    </div>

                    <Container className="relative z-10">
                        <div className="max-w-4xl mx-auto text-center space-y-8 animate-in fade-in-0 duration-1000 delay-300">
                            <Typography
                                variant="h1"
                                className="text-5xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-foreground via-foreground/90 to-foreground/70 bg-clip-text text-transparent leading-tight tracking-tight"
                            >
                                {t('hero.title')}
                            </Typography>

                            <Typography variant="lead" className="max-w-3xl mx-auto text-xl text-muted-foreground leading-relaxed">
                                {t('hero.description')}
                            </Typography>
                        </div>
                    </Container>
                </section>

                {/* Blog Posts Section */}
                <section className="pt-4 pb-16 bg-muted/30">
                    <Container>
                        <div className="grid lg:grid-cols-2 gap-8 mb-12">
                            {BLOG_POSTS.map((post, index) => (
                                <Link key={post.href[locale]} href={getNavigationPath((post as any).postData.id)}>
                                    <Card
                                        variant="elevated"
                                        className="h-full hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
                                    >
                                        {/* Image */}
                                        <div className="h-48 overflow-hidden relative">
                                            <Image
                                                src={(post as any).postData.imageUrl}
                                                alt={post.meta.title[locale]}
                                                fill
                                                className="object-cover group-hover:scale-105 transition-transform duration-300"
                                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                            />
                                        </div>

                                        <CardContent className="p-6">
                                            {/* Category and Date */}
                                            <div className="flex items-center justify-between mb-4">
                                                <Badge variant="secondary" className="text-xs">
                                                    {(post as any).postData.categories[locale]}
                                                </Badge>
                                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                                    <Calendar className="h-4 w-4" />
                                                    {new Date((post as any).postData.date).toLocaleDateString(locale, {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </div>
                                            </div>

                                            {/* Title */}
                                            <CardTitle className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2">
                                                {post.meta.title[locale]}
                                            </CardTitle>

                                            {/* Excerpt */}
                                            <CardDescription className="text-muted-foreground mb-4 leading-relaxed line-clamp-3">
                                                {post.meta.description[locale]}
                                            </CardDescription>

                                            {/* Tags and Read Time */}
                                            <CardFooter className="p-0 flex items-center justify-between">
                                                <div className="flex flex-wrap gap-2">
                                                    {post.meta.tags[locale].slice(0, 3).map((tag) => (
                                                        <Badge key={tag} variant="outline" className="text-xs">
                                                            <Tag className="h-3 w-3 mr-1" />
                                                            {tag}
                                                        </Badge>
                                                    ))}
                                                </div>
                                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                                    <Clock className="h-4 w-4" />
                                                    {(post as any).postData.readTime[locale]}
                                                </div>
                                            </CardFooter>
                                        </CardContent>
                                    </Card>
                                </Link>
                            ))}
                        </div>

                        {/* Back to Home Button */}
                        <div className="text-center">
                            <Button
                                asChild
                                variant="outline"
                                size="lg"
                                className="group border-2 border-primary/20 hover:border-primary/40 bg-gradient-to-r from-primary/5 to-primary/10 hover:from-primary/10 hover:to-primary/20 text-primary font-semibold shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                            >
                                <Link href={getNavigationPath('home')} className='flex items-center'>
                                    <Home className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                                    {t('buttons.backToHome')}
                                </Link>
                            </Button>
                        </div>
                    </Container>
                </section>
            </div>

            <SchemaScript route={Routes.blog} locale={locale as Locale} />
        </>
    );
} 